import * as React from 'react';
import {
  comparisonReportProvider,
  IComparisonReportState,
} from '../../../data/providers/reports/comparisonReportProvider';
import { Entity, EntityType, ISelectableTag, ITag } from '../../../data/types';
import { testMetaString } from '../../../utils/testMetaString';
import { Column } from '../../layout/Column';
import { LinearLoading } from '../../shared/loading/LinearLoading';
import { EPdfMode, isPdf, Pdf } from '../../shared/pdf/Pdf';
import { PdfContainer } from '../../shared/pdf/PdfContainer';
import { colors } from '../colors';
import { ErrorMessage } from '../utils/ErrorMessages';
import { ReportOverview } from '../utils/ReportOverview';
import { useFilename } from '../utils/useFilename';
import { ComparisonGraph } from './ComparisonGraph';
import { ResultList } from './ResultList';
import { ComparisonReportSettings } from './settings/Settings';

interface IProps {
  pdfMode?: EPdfMode;
  entity: Entity;
  entityType: EntityType;
  onPdfModeChange: (pdfMode: EPdfMode) => void;
  loading: boolean;
  setLoading: (v: boolean) => void;
}

export const ComparisonReport = (props: IProps): React.ReactElement => {
  const [reportState, setReportState] =
    React.useState<IComparisonReportState>();
  /**
   * @description subscribe to report state
   */
  React.useEffect((): (() => void) => {
    props.setLoading(true);
    const subscriber = comparisonReportProvider(
      props.entityType,
      props.entity.id
    ).subscribe((state: IComparisonReportState): void => {
      setReportState(state);
      props.setLoading(false);
    });
    return (): void => subscriber.unsubscribe();
  }, [props.entity?.id]);

  /**
   * @description make filename for export
   */
  const filename = useFilename(
    'comparison',
    props.entity,
    reportState?.timeframe
  );

  const targetSecondaryText = React.useMemo((): string => {
    return testMetaString(
      reportState?.selected?.settings?.testType?.entity?.abbr,
      reportState?.selected?.settings?.target?.tags?.map(
        (st: ISelectableTag): ITag => st.entity
      )
    );
  }, [JSON.stringify(reportState?.selected?.settings)]);

  const populationSecondaryText = React.useMemo((): string => {
    return testMetaString(
      reportState?.selected?.settings?.testType?.entity?.abbr,
      reportState?.selected?.settings?.population?.tags?.map(
        (st: ISelectableTag): ITag => st.entity
      )
    );
  }, [JSON.stringify(reportState?.selected?.settings)]);

  return (
    <React.Fragment>
      {reportState?.isValid && !props.loading ? (
        isPdf(props.pdfMode) ? (
          <PdfContainer>
            <Pdf
              onComplete={() => {}}
              filename={filename}
              title='Comparison Report'
              entity={props.entity}
              loading={props.loading}
              reportState={reportState}
              onPdfModeChange={props.onPdfModeChange}
              pdfMode={props.pdfMode}
            >
              <Column>
                <ReportOverview
                  targetSecondary={targetSecondaryText}
                  populationSecondary={populationSecondaryText}
                  populationTimeframe={{
                    dateType: reportState?.comparison?.dateRangeType,
                    dateRange: reportState?.comparison?.dateRangeValues,
                  }}
                  populationEntityName={reportState.comparison?.entity?.label}
                  targetTimeframe={{
                    dateType: reportState?.targetTimeframe?.dateType,
                    dateRange: reportState?.targetTimeframe?.dateRange,
                  }}
                  targetEntity={props.entity}
                  reportName={`Comparison Report - ${reportState?.selected?.name}`}
                  isPdf={isPdf(props.pdfMode)}
                />
                <ComparisonGraph
                  results={reportState.data}
                  pdfMode={props.pdfMode}
                  colors={colors}
                />
                <ResultList
                  results={reportState.data}
                  colors={colors}
                  pdfMode={props.pdfMode}
                  entity={props.entity}
                  comparisonEntityName={reportState.comparison?.entity?.label}
                />
              </Column>
            </Pdf>
          </PdfContainer>
        ) : (
          <Column>
            {!props.loading ? (
              <React.Fragment>
                <ReportOverview
                  targetSecondary={targetSecondaryText}
                  populationSecondary={populationSecondaryText}
                  populationTimeframe={{
                    dateType: reportState?.comparison?.dateRangeType,
                    dateRange: reportState?.comparison?.dateRangeValues,
                  }}
                  populationEntityName={reportState.comparison?.entity?.label}
                  targetTimeframe={{
                    dateType: reportState?.targetTimeframe?.dateType,
                    dateRange: reportState?.targetTimeframe?.dateRange,
                  }}
                  targetEntity={props.entity}
                  reportName={`Comparison Report - ${reportState?.selected?.name}`}
                  isPdf={isPdf(props.pdfMode)}
                />
                <ComparisonGraph
                  results={reportState.data}
                  pdfMode={props.pdfMode}
                  colors={colors}
                />
                <ResultList
                  results={reportState.data}
                  colors={colors}
                  pdfMode={props.pdfMode}
                  entity={props.entity}
                  comparisonEntityName={reportState.comparison?.entity?.label}
                />
              </React.Fragment>
            ) : (
              <LinearLoading />
            )}
          </Column>
        )
      ) : props.loading ? (
        <LinearLoading />
      ) : (
        <Error
          errorCode={reportState?.errorCode}
          setLoading={props.setLoading}
        />
      )}
    </React.Fragment>
  );
};

const Error = ({
  errorCode,
  setLoading,
}: {
  errorCode?: string;
  setLoading: (v: boolean) => void;
}): React.ReactElement => {
  const onSave = (): void => setLoading(true);
  switch (errorCode) {
    case 'missing-template':
      return (
        <ErrorMessage iconName='window' text='Please Select a Template'>
          <ComparisonReportSettings
            style={{ marginTop: '10px', maxWidth: 'max-content' }}
            onSave={onSave}
          />
        </ErrorMessage>
      );
    case 'missing-metrics':
      return (
        <ErrorMessage iconName='poll' text='Missing Metrics'>
          <div style={{ marginTop: '10px' }}>
            Please select your desired metrics in settings.
          </div>
          <ComparisonReportSettings
            style={{ marginTop: '10px' }}
            onSave={onSave}
          />
        </ErrorMessage>
      );

    case 'missing-test-type':
      return (
        <ErrorMessage iconName='accessibility_new' text='Select a Test Type'>
          <ComparisonReportSettings
            style={{ marginTop: '10px' }}
            onSave={onSave}
          />
        </ErrorMessage>
      );
    case 'dataset-b-settings-invalid':
      return (
        <ErrorMessage
          iconName='error_outline'
          text='Data Set B Settings Invalid'
        >
          <div style={{ marginTop: '10px' }}>
            Please configure Data Set B in settings.
          </div>
          <ComparisonReportSettings
            style={{ marginTop: '10px' }}
            onSave={onSave}
          />
        </ErrorMessage>
      );
    case 'dataset-a-invalid':
      return (
        <ErrorMessage iconName='error_outline' text='Not Enough Data'>
          <div style={{ marginTop: '10px' }}>
            Data Set A does not have enough data during the selected time period
            to produce this report. Try changing the timeframe.
          </div>
          <ComparisonReportSettings
            style={{ marginTop: '10px' }}
            onSave={onSave}
          />
        </ErrorMessage>
      );
    case 'dataset-b-invalid':
      return (
        <ErrorMessage iconName='error_outline' text='Not Enough Data'>
          <div style={{ marginTop: '10px' }}>
            Data Set B does not have enough data during the selected time period
            to produce this report. Try changing either the timeframe or
            choosing a different comparison.
          </div>
          <ComparisonReportSettings
            style={{ marginTop: '10px' }}
            onSave={onSave}
          />
        </ErrorMessage>
      );
    default:
      return null;
  }
};
